<template>
  <div class="purchase-apply-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('purchaseApplyManage.page.title')" :subtitle="$t('purchaseApplyManage.page.subtitle')">
      <template #extra>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="_openAddPurchaseApplyModal()">
          {{ $t('purchaseApplyManage.button.purchase') }}
        </el-button>
        <el-button type="primary" size="small" v-if="hasPrivilege('502021071043650029')"
          icon="el-icon-plus" @click="_openUrgentPurchaseApplyModal()">
          {{ $t('purchaseApplyManage.button.urgentPurchase') }}
        </el-button>
        <el-button size="small" icon="el-icon-download" @click="_exportExcel()">
          {{ $t('common.export') }}
        </el-button>
      </template>
    </ds-page-header>

    <el-row :gutter="16">
      <el-col :xs="24" :sm="6" :md="5" :lg="4">
        <div class="ds-panel ds-panel--padded">
          <div class="ds-panel__header">
            <span class="ds-panel__title">{{ $t('purchaseApplyManage.stateFilter') }}</span>
          </div>
          <ul class="ds-state-list">
            <li v-for="(item, index) in purchaseApplyManageInfo.states" :key="index" @click="swatchState(item)"
              :class="{ 'is-active': purchaseApplyManageInfo.conditions.state === item.statusCd }">
              {{ item.name }}
            </li>
          </ul>
        </div>
      </el-col>
      <el-col :xs="24" :sm="18" :md="19" :lg="20">
        <!-- (2) SearchPanel -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input :placeholder="$t('purchaseApplyManage.search.applyOrderId')" clearable class="w-full"
                v-model.trim="purchaseApplyManageInfo.conditions.applyOrderId" @keyup.enter.native="_queryPurchaseApplyMethod()" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input :placeholder="$t('purchaseApplyManage.search.name')" clearable class="w-full"
                v-model.trim="purchaseApplyManageInfo.conditions.name" @keyup.enter.native="_queryPurchaseApplyMethod()" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input :placeholder="$t('purchaseApplyManage.search.resName')" clearable class="w-full"
                v-model.trim="purchaseApplyManageInfo.conditions.resName" @keyup.enter.native="_queryPurchaseApplyMethod()" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker v-model="purchaseApplyManageInfo.conditions.startTime" type="datetime"
                value-format="yyyy-MM-dd HH:mm:ss" :placeholder="$t('purchaseApplyManage.search.startTime')" style="width: 100%" />
            </el-col>
            <el-col v-show="moreCondition" :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker v-model="purchaseApplyManageInfo.conditions.endTime" type="datetime"
                value-format="yyyy-MM-dd HH:mm:ss" :placeholder="$t('purchaseApplyManage.search.endTime')" style="width: 100%" />
            </el-col>
          </el-row>
          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="_queryPurchaseApplyMethod()">
              {{ $t('common.search') }}
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="_resetInspectionPlanMethod()">
              {{ $t('common.reset') }}
            </el-button>
            <el-button type="text" @click="moreCondition = !moreCondition">
              {{ moreCondition ? $t('purchaseApplyManage.hide') : $t('purchaseApplyManage.more') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="page.total" :density="density" :show-column-setting="false"
            :show-refresh="true" @update:density="density = $event" @refresh="_listPurchaseApplys(page.current, page.size)">
            <template #left>
              <span class="ds-panel__title">{{ $t('purchaseApplyManage.list.title') }}</span>
            </template>
          </ds-toolbar>

          <ds-data-table :data="purchaseApplyManageInfo.purchaseApplys" :loading="loading" :error="hasError"
            :error-message="errorMessage" :density="density" border
            :empty-description="$t('purchaseApplyManage.page.empty')" @retry="_listPurchaseApplys(page.current, page.size)">
            <el-table-column prop="applyOrderId" :label="$t('purchaseApplyManage.table.applyOrderId')" align="center" width="170">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.applyOrderId || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="userName" :label="$t('purchaseApplyManage.table.userName')" align="center" min-width="100" />
            <el-table-column prop="endUserName" :label="$t('purchaseApplyManage.table.endUserName')" align="center" min-width="100" />
            <el-table-column prop="createUserName" :label="$t('purchaseApplyManage.table.createUserName')" align="center" min-width="100" />
            <el-table-column prop="resourceNames" :label="$t('purchaseApplyManage.table.resourceNames')" align="center" min-width="180" show-overflow-tooltip />
            <el-table-column prop="createTime" :label="$t('purchaseApplyManage.table.createTime')" align="center" width="170">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.createTime || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('purchaseApplyManage.table.warehousingWay')" align="center" width="120">
              <template slot-scope="scope">
                <ds-status-tag size="sm"
                  :type="scope.row.warehousingWay == 20000 ? 'info' : scope.row.warehousingWay == 30000 ? 'warning' : 'muted'"
                  :label="scope.row.warehousingWay == 10000
                    ? $t('purchaseApplyManage.warehousingWay.direct')
                    : scope.row.warehousingWay == 20000
                      ? $t('purchaseApplyManage.warehousingWay.purchase')
                      : $t('purchaseApplyManage.warehousingWay.urgent')" />
              </template>
            </el-table-column>
            <el-table-column prop="stateName" :label="$t('purchaseApplyManage.table.stateName')" align="center" width="110">
              <template slot-scope="scope">
                <ds-status-tag size="sm" :type="scope.row.state == '1000' ? 'warning' : scope.row.state == '2000' ? 'success' : 'muted'"
                  :label="scope.row.stateName || '-'" />
              </template>
            </el-table-column>
            <el-table-column :label="$t('common.operation')" align="center" width="260" fixed="right">
              <template slot-scope="scope">
                <el-button size="mini" @click="_openDetailPurchaseApplyModel(scope.row)">
                  {{ $t('common.view') }}
                </el-button>
                <el-button size="mini" v-if="scope.row.warehousingWay != 10000 && scope.row.warehousingWay != 30000"
                  @click="_openRunWorkflowImage(scope.row)">
                  {{ $t('purchaseApplyManage.button.flowChart') }}
                </el-button>
                <el-button size="mini" type="danger" v-if="scope.row.state == '1000' &&
                  purchaseApplyManageInfo.currentUserId == scope.row.createUserId"
                  @click="_openDeletePurchaseApplyModel(scope.row)">
                  {{ $t('purchaseApplyManage.button.cancelApply') }}
                </el-button>
                <el-button size="mini" type="danger" v-else-if="scope.row.state == '1000' &&
                  hasPrivilege('502022032383620003')" @click="_openDeletePurchaseApplyModel(scope.row)">
                  {{ $t('purchaseApplyManage.button.cancelApply') }}
                </el-button>
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar :page="page.current" :size="page.size" :total="page.total"
            :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange" />
        </div>
      </el-col>
    </el-row>

    <view-image ref="viewImage" />
    <edit-purchase-apply ref="editPurchaseApply" />
    <delete-purchase-apply ref="deletePurchaseApply" @success="_listPurchaseApplys(page.current, page.size)" />
  </div>
</template>

<script>
import { listPurchaseApplys, exportData, getWorkflowImage } from '@/api/resource/purchaseApplyManageApi'
import { getDict } from '@/api/community/communityApi'
import { getCommunityId } from '@/api/community/communityApi'
import ViewImage from '@/components/system/viewImage'
import EditPurchaseApply from '@/components/resource/editPurchaseApply'
import DeletePurchaseApply from '@/components/resource/deletePurchaseApply'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'PurchaseApplyManageList',
  components: {
    ViewImage,
    EditPurchaseApply,
    DeletePurchaseApply,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar,
    DsStatusTag
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      moreCondition: false,
      purchaseApplyManageInfo: {
        purchaseApplys: [],
        total: 0,
        records: 1,
        moreCondition: false,
        states: [],
        currentUserId: '',
        conditions: {
          state: '',
          applyOrderId: '',
          userName: '',
          name: '',
          resOrderType: '10000',
          resName: '',
          startTime: '',
          endTime: '',
          userId: '',
          communityId: ''
        }
      },
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.purchaseApplyManageInfo.currentUserId = ''
    this.purchaseApplyManageInfo.conditions.userId = ''
    this.purchaseApplyManageInfo.conditions.communityId = getCommunityId()
    this._listPurchaseApplys(this.page.current, this.page.size)
    this.getDictData()
  },
  methods: {
    async getDictData() {
      try {
        const data = await getDict('purchase_apply', 'state')
        this.purchaseApplyManageInfo.states = [
          {
            statusCd: '',
            name: this.$t('common.all')
          },
          ...data
        ]
      } catch (error) {
        console.error('获取字典数据失败:', error)
      }
    },
    async _listPurchaseApplys(page, size) {
      this.loading = true
      this.hasError = false
      try {
        const params = {
          ...this.purchaseApplyManageInfo.conditions,
          page,
          row: size
        }
        const { purchaseApplys, total } = await listPurchaseApplys(params)
        this.purchaseApplyManageInfo.purchaseApplys = purchaseApplys
        this.page.total = total
      } catch (error) {
        console.error('获取采购申请列表失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('common.loadError')
      } finally {
        this.loading = false
      }
    },
    _openAddPurchaseApplyModal() {
      this.$router.push({
        path: '/views/resource/addPurchaseApply',
        query: {
          resOrderType: this.purchaseApplyManageInfo.conditions.resOrderType
        }
      })
    },
    _openUrgentPurchaseApplyModal() {
      this.$router.push({
        path: '/views/resource/urgentPurchaseApplyStep',
        query: {
          resOrderType: this.purchaseApplyManageInfo.conditions.resOrderType
        }
      })
    },
    _openDetailPurchaseApplyModel(purchaseApply) {
      this.$router.push({
        path: '/views/resource/purchaseApplyDetail',
        query: {
          applyOrderId: purchaseApply.applyOrderId,
          resOrderType: '10000'
        }
      })
    },
    _openDeletePurchaseApplyModel(purchaseApply) {
      this.$refs.deletePurchaseApply.open(purchaseApply)
    },
    _queryPurchaseApplyMethod() {
      this.page.current = 1
      this._listPurchaseApplys(this.page.current, this.page.size)
    },
    _resetInspectionPlanMethod() {
      this.purchaseApplyManageInfo.conditions = {
        ...this.purchaseApplyManageInfo.conditions,
        applyOrderId: '',
        name: '',
        resName: '',
        startTime: '',
        endTime: ''
      }
      this._listPurchaseApplys(this.page.current, this.page.size)
    },
    async _openRunWorkflowImage(purchaseApply) {
      try {
        const params = {
          businessKey: purchaseApply.applyOrderId,
          communityId: getCommunityId()
        }
        const { data } = await getWorkflowImage(params)
        this.$refs.viewImage.open({
          url: 'data:image/png;base64,' + data
        })
      } catch (error) {
        console.error('获取流程图失败:', error)
      }
    },
    async _exportExcel() {
      try {
        const params = {
          ...this.purchaseApplyManageInfo.conditions,
          pagePath: 'purchaseApplyManage'
        }
        await exportData(params)
        this.$message.success(this.$t('common.operationSuccess'))
        this.$router.push('/pages/property/downloadTempFile?tab=下载中心')
      } catch (error) {
        console.error('导出失败:', error)
      }
    },
    swatchState(item) {
      this.purchaseApplyManageInfo.conditions.state = item.statusCd
      this._listPurchaseApplys(this.page.current, this.page.size)
    },
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this._listPurchaseApplys(this.page.current, this.page.size)
    },
    handleCurrentChange(val) {
      this.page.current = val
      this._listPurchaseApplys(this.page.current, this.page.size)
    }
  }
}
</script>

<style lang="scss" scoped>
.purchase-apply-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-lg);

  &--padded {
    padding: var(--ds-spacing-lg);
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: var(--ds-spacing-md);
    margin-bottom: var(--ds-spacing-md);
    border-bottom: 1px solid var(--ds-gray-200);
  }

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

.ds-state-list {
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    padding: var(--ds-spacing-sm) var(--ds-spacing-md);
    font-size: var(--ds-font-size-sm);
    color: var(--ds-gray-600);
    border-radius: var(--ds-radius-sm);
    cursor: pointer;
    text-align: center;
    margin-bottom: var(--ds-spacing-xs);

    &:hover {
      background: var(--ds-gray-100);
      color: var(--ds-primary);
    }

    &.is-active {
      background: var(--ds-primary-sub);
      color: var(--ds-primary);
      font-weight: 600;
    }
  }
}

.w-full {
  width: 100%;
}
</style>
